import { FunctionComponent, useEffect, useContext, useCallback } from "react"
import { useHistory, useParams } from "react-router-dom"
import SummaryOptions from "components/Summary/SummaryOptions"
import IPage from "interfaces/IPage"
import { IconSpinner, IconPackage, IconCheck, IconTruckDelivery } from "theme/common/Icon.styled"
import { useContractor } from "hooks"
import { AuthContext } from "context/context/AuthContext"
import { EShippinggroupStatus } from "enums/shippinggroupEmun"
import { ContractorMenuItem } from "interfaces/IContractor"
import { ContractorTypeEnum } from "enums/ContractorEnum"
import Multiclient from "components/Core/Multiclient"
import { ISearchResults } from "interfaces/ISearchResults"
import BackToDashboardButton from "components/Commons/BackToDashboardButton/BackToDashboardButton"

const ContractorSubStatusScreen: FunctionComponent<IPage> = (props: any) => {
  const {
    state: { user },
  } = useContext(AuthContext)

  const history = useHistory()

  // Obtener el parámetro :status de la URL
  const { type } = useParams<{ type: string }>()
  
  // Validar si el type existe en el enum
  const typeValue: ContractorTypeEnum | undefined = type ? ContractorTypeEnum[type as keyof typeof ContractorTypeEnum] : undefined
  const isValidType = typeValue !== undefined
  
  const {
    summaryMenuOrder,
    loading,
    getSources,
    getCTOChannels,
    loadContractorData
  } = useContractor()

  const getSummaryMenuOrder = (): ContractorMenuItem[] => {
    const baseMenuItems: ContractorMenuItem[] = [
      {
        title: "Pendientes",
        path: `/contractor/${type}/order/new`,
        icon: <IconCheck />,
        status: EShippinggroupStatus.transit,
        optionCount: 0,
        body: {
          size: 1,
          from: 0,
          fields:["orderId"],
          filters: {
            shippingType : "TR",
            "custom.infoCTO.orderType" : typeValue as string,
            "custom.infoCTO.statusCTO" : "Nueva",
            "currentStatus.tag":["IN_TRANSIT"],
            channel: getCTOChannels(),
            "target.source.id": user?.allSources ? undefined : getSources() || [],
          }
        }
      },
      {
        title: "Recibidas con diferencias",
        path: `/contractor/${type}/order/incomplete`,
        icon: <IconPackage />,
        status: EShippinggroupStatus.delivered,
        optionCount: 0,
        body: {
          size: 1,
          from: 0,
          fields:["orderId"],
          filters: {
            shippingType : "TR",
            "custom.infoCTO.orderType" : typeValue as string,
            "custom.infoCTO.statusCTO" : "Incompleta",
            "currentStatus.tag":["DELIVERED"],
            channel: getCTOChannels(),
            "target.source.id": user?.allSources ? undefined : getSources() || [],
          }
        },
      },
      {
        title: "Recibidas completas",
        path: `/contractor/${type}/order/complete`,
        icon: <IconTruckDelivery />,
        status: EShippinggroupStatus.delivered,
        optionCount: 0,
        body: {
          size: 1,
          from: 0,
          fields:["orderId"],
          filters: {
            shippingType : "TR",
            "custom.infoCTO.orderType" : typeValue as string,
            "custom.infoCTO.statusCTO" : "Completa",
            "currentStatus.tag":["DELIVERED"],
            channel: getCTOChannels(),
            "target.source.id": user?.allSources ? undefined : getSources() || [],
          }
        },
      }
    ]

    if (type === 'installation') {
      return [
        {
          title: "Pendientes",
          path: `/contractor/${type}/order/new`,
          icon: <IconCheck />,
          status: EShippinggroupStatus.transit,
          optionCount: 0,
          body: {
            size: 1,
            from: 0,
            fields:["orderId"],
            filters: {
              shippingType : "TR",
              "custom.infoCTO.orderType" : typeValue as string,
              "custom.infoCTO.statusCTO" : "Nueva",
              "currentStatus.tag":["AWAITING_STORE_CONFIRMATION"],
              channel: getCTOChannels(),
              "source.id": user?.allSources ? undefined : getSources() || [],
            }
          }
        },
        {
          title: "Instalaciones en Curso",
          path: `/contractor/${type}/order/incomplete`,
          icon: <IconPackage />,
          status: EShippinggroupStatus.transit,
          optionCount: 0,
          body: {
            size: 1,
            from: 0,
            fields:["orderId"],
            filters: {
              shippingType : "TR",
              "custom.infoCTO.orderType" : typeValue as string,
              "custom.infoCTO.statusCTO" : "Incompleta",
              "currentStatus.tag":["AWAITING_STORE_CONFIRMATION"],
              channel: getCTOChannels(),
              "source.id": user?.allSources ? undefined : getSources() || [],
            }
          },
        },
        {
          title: "Instalaciones completadas",
          path: `/contractor/${type}/order/complete`,
          icon: <IconTruckDelivery />,
          status: EShippinggroupStatus.delivered,
          optionCount: 0,
          body: {
            size: 1,
            from: 0,
            fields:["orderId"],
            filters: {
              shippingType : "TR",
              "custom.infoCTO.orderType" : typeValue as string,
              "custom.infoCTO.statusCTO" : "Completa",
              "currentStatus.tag":["DELIVERED"],
              channel: getCTOChannels(),
              "source.id": user?.allSources ? undefined : getSources() || [],
            }
          },
        }
      ]
    }

    // Retornar el menú base si no coincide con ningún tipo específico
    return baseMenuItems
  }

  const handleClickSearch = useCallback(
    ({ query, optionFilter }: ISearchResults) => {
      if (query) history.push(`/contractor/search/${query}/${optionFilter}`)
    },
    [history],
  )

  useEffect(() => {    
    const cardItems = getSummaryMenuOrder()
    loadContractorData(cardItems)
  },
  //eslint-disable-next-line
  [user, type])

  // Si el tipo no es válido, mostrar mensaje de error
  if (!isValidType) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '2rem', 
        gap: '1rem',
        minHeight: '400px'
      }}>
        <div style={{ 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '8px', 
          padding: '1.5rem', 
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#c33', fontSize: '1.2rem' }}>
            Tipo de orden inválido
          </h3>
          <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '1rem' }}>
            El tipo "{type}" no es válido.
          </p>
          <p style={{ margin: 0, color: '#888', fontSize: '0.9rem' }}>
            Los tipos válidos son: <strong>reception</strong> o <strong>installation</strong>
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '2rem', gap: '1rem' }}>
          <IconSpinner variant="big" />
          <p style={{ margin: 0, fontSize: '1.1rem', color: '#666', textAlign: 'center' }}>
            Cargando información de bandejas...
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#999', textAlign: 'center' }}>
            Obteniendo conteos de órdenes...
          </p>
        </div>
      ) : (
        <>
          <BackToDashboardButton />
          <Multiclient
            path_override="overrides/theme/wom/base/components/SearchForm/SearchForm"
            onClickSearch={handleClickSearch}
          />
          {summaryMenuOrder && <SummaryOptions summaryOptions={summaryMenuOrder as any} contractor={true} />}
        </>
      )}
    </>
  )
}

export default ContractorSubStatusScreen
